import { useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import type { RepoHierarchyPickerProps } from './RepoHierarchyPicker';
import { useAsyncOptions } from './useAsyncOptions';

export function useRepositoryOptions({
  provider,
  value,
}: RepoHierarchyPickerProps) {
  const api = useApi(spassuCodeApiRef);
  const { owner, project, repository } = value;
  const owners = useAsyncOptions(
    useCallback(() => api.listScmOwners(provider.id), [api, provider.id]),
  );
  const validOwner = owners.items.some(item => item.id === owner?.id);
  const projects = useAsyncOptions(
    useCallback(
      () => api.listScmProjects(provider.id, owner!.id),
      [api, provider.id, owner],
    ),
    validOwner && provider.hierarchy.hasProject,
  );
  const validProject =
    !provider.hierarchy.hasProject ||
    projects.items.some(item => item.id === project?.id);
  const repositories = useAsyncOptions(
    useCallback(
      () => api.listScmRepositories(provider.id, owner!.id, project?.name),
      [api, provider.id, owner, project],
    ),
    validOwner && validProject,
  );
  const validRepository = repositories.items.some(
    item => item.externalId === repository?.externalId,
  );
  const branches = useAsyncOptions(
    useCallback(
      () =>
        api.listScmBranches(provider.id, {
          externalId: repository!.externalId,
          owner: repository!.owner,
          project: repository!.project,
          name: repository!.name,
        }),
      [api, provider.id, repository],
    ),
    validRepository,
  );
  return { owners, projects, repositories, branches };
}
